import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowLeft, Shield, Copy, Check, MessageCircle, X, RotateCcw } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { formatTypographicQuotes } from "@/lib/typographic-quotes";
import { AtmosphericPage } from "@/components/MirrorAtmosphere";

interface UserEntry {
  id: string;
  email: string;
  displayName: string | null;
  createdAt: string;
  totalMessages: number;
  recentMessages: number;
  firstLimitHitDate: string | null;
  hitLimit: boolean;
  dailyMessageCount: number;
  messageCountResetDate: string | null;
}

interface DayActivity {
  date: string;
  messages: number;
  users: number;
}

interface AdminStats {
  totalUsers: number;
  newSignups: number;
  totalMessages: number;
  activeInPeriod: number;
  dailyActivity: DayActivity[];
  userList: UserEntry[];
  days: number;
}

interface ChatMsg {
  role: string;
  content: string;
  createdAt: string;
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTime(d: string) {
  return new Date(d).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function formatDayHeader(d: string) {
  return new Date(d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function AnimNum({ value }: { value: number }) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const diff = value - start;
    if (diff === 0) return;
    const t0 = performance.now();
    const run = (now: number) => {
      const p = Math.min((now - t0) / 500, 1);
      setDisplay(Math.round(start + diff * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(run);
      else prev.current = value;
    };
    requestAnimationFrame(run);
  }, [value]);
  return <>{display.toLocaleString()}</>;
}

function AdminAtmosphere({ children }: { children: React.ReactNode }) {
  return (
    <AtmosphericPage
      contained
      atmosphere={{ scrimVariant: "content" }}
      className="mirror-atmosphere-page--scrollable"
      contentClassName="min-h-full"
    >
      {children}
    </AtmosphericPage>
  );
}

export default function Admin() {
  const [, setLocation] = useLocation();
  const [days, setDays] = useState(14);
  const [copied, setCopied] = useState(false);
  const [viewingUser, setViewingUser] = useState<UserEntry | null>(null);

  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"] });
  const { data: stats, isLoading, error, dataUpdatedAt } = useQuery<AdminStats>({
    queryKey: ["/api/admin/stats", days],
    queryFn: () => fetch(`/api/admin/stats?days=${days}`, { credentials: "include" }).then(r => r.json()),
    enabled: !!user?.isAdmin,
    refetchInterval: 30000,
  });

  if (!user?.isAdmin) {
    return (
      <AdminAtmosphere>
        <div className="flex items-center justify-center min-h-full" style={{ color: "var(--text-muted)" }}>
          <p style={{ fontFamily: "var(--font-reflective)" }}>Not authorized</p>
        </div>
      </AdminAtmosphere>
    );
  }

  if (isLoading) {
    return (
      <AdminAtmosphere>
        <div className="flex items-center justify-center min-h-full" style={{ color: "var(--text-muted)" }}>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-label-size)", letterSpacing: "var(--type-label-tracking)" }}>LOADING</p>
        </div>
      </AdminAtmosphere>
    );
  }

  if (error || !stats) {
    return (
      <AdminAtmosphere>
        <div className="flex items-center justify-center min-h-full" style={{ color: "var(--text-muted)" }}>
          <p style={{ fontFamily: "var(--font-reflective)" }}>Failed to load data</p>
        </div>
      </AdminAtmosphere>
    );
  }

  if (viewingUser) {
    return (
      <AdminAtmosphere>
        <ConversationView user={viewingUser} onBack={() => setViewingUser(null)} />
      </AdminAtmosphere>
    );
  }

  const activeUsers = stats.userList.filter(u => u.recentMessages > 0);
  const otherUsers = stats.userList.filter(u => u.recentMessages === 0);
  const periodMsgs = stats.dailyActivity.reduce((s, d) => s + d.messages, 0);
  const avgDaily = stats.dailyActivity.length > 0 ? Math.round(periodMsgs / stats.dailyActivity.length) : 0;
  const periodLabel = `${days}d`;
  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";

  const copyEmails = () => {
    const emails = activeUsers.map(u => u.email).join("\n");
    navigator.clipboard.writeText(emails).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <AdminAtmosphere>
      <div className="admin-page-shell w-full max-w-lg mx-auto px-5 py-6" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setLocation("/")}
          className="p-1"
          style={{ color: "var(--text-muted)" }}
          data-testid="button-admin-back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center gap-2">
          <Shield size={13} style={{ color: "var(--accent-text)" }} />
          <span className="type-label" style={{ color: "var(--accent-text)" }}>
            Admin
          </span>
        </div>
        <span
          style={{
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--type-label-size)",
            letterSpacing: "var(--type-label-tracking)",
          }}
        >
          {lastUpdated}
        </span>
      </div>

      <div
        className="flex gap-1 mb-5 p-0.5 rounded-lg"
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border-hairline)", width: "fit-content" }}
      >
        {[7, 14, 30].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className="px-3 py-1 rounded transition-all"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "0.75rem",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              background: days === d ? "var(--accent-text)" : "transparent",
              color: days === d ? "var(--page-bg)" : "var(--text-muted)",
              fontWeight: days === d ? 600 : 400,
            }}
            data-testid={`button-period-${d}`}
          >
            {d}d
          </button>
        ))}
      </div>

      <div
        className="rounded-xl p-4 mb-6"
        style={{ background: "var(--card-bg)", border: "1px solid var(--border-hairline)" }}
      >
        <div className="grid grid-cols-3 gap-4 mb-3 pb-3" style={{ borderBottom: "1px solid var(--border-hairline)" }}>
          <Stat label="Users" value={stats.totalUsers} />
          <Stat label={`New (${periodLabel})`} value={stats.newSignups} accent={stats.newSignups > 0} />
          <Stat label={`Active (${periodLabel})`} value={stats.activeInPeriod} accent={stats.activeInPeriod > 0} />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Stat label="All Messages" value={stats.totalMessages} />
          <Stat label={`Msgs (${periodLabel})`} value={periodMsgs} />
          <Stat label="Avg / Day" value={avgDaily} />
        </div>
      </div>

      <div className="mb-2">
        <div className="flex items-center justify-between mb-3">
          <span
            style={{
              color: "var(--fg-raw)",
              fontFamily: "var(--font-mono)",
              fontSize: "var(--type-label-size)",
              letterSpacing: "var(--type-label-tracking)",
              textTransform: "uppercase",
            }}
          >
            Active · {periodLabel}
          </span>
          <div className="flex items-center gap-2">
            {activeUsers.length > 0 && (
              <button
                onClick={copyEmails}
                className="flex items-center gap-1 px-2 py-0.5 rounded transition-all"
                style={{
                  color: copied ? "var(--accent-text)" : "var(--text-muted)",
                  fontFamily: "var(--font-display)",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  border: "1px solid var(--border-hairline)",
                }}
                data-testid="button-copy-emails"
              >
                {copied ? <Check size={9} /> : <Copy size={9} />}
                {copied ? "Copied" : "Emails"}
              </button>
            )}
            <span
              style={{
                color: "var(--text-muted)",
                fontFamily: "var(--font-display)",
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              {activeUsers.length}
            </span>
          </div>
        </div>

        {activeUsers.length === 0 ? (
          <p
            className="text-center py-4"
            style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
          >
            No active users in this period
          </p>
        ) : (
          <div className="space-y-1.5">
            {activeUsers.map((u, i) => (
              <UserRow key={u.email} u={u} periodLabel={periodLabel} rank={i + 1} onView={() => setViewingUser(u)} />
            ))}
          </div>
        )}
      </div>

      {otherUsers.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <span
              style={{
                color: "var(--text-muted)",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--type-label-size)",
                letterSpacing: "var(--type-label-tracking)",
                textTransform: "uppercase",
              }}
            >
              {otherUsers.some(u => u.totalMessages > 0) ? "Inactive" : "Other"}
            </span>
            <span
              style={{
                color: "var(--text-muted)",
                fontFamily: "var(--font-display)",
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              {otherUsers.length}
            </span>
          </div>
          <div className="space-y-1">
            {otherUsers.map(u => (
              <div
                key={u.email}
                className="flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer"
                style={{ background: "var(--card-bg)", border: "1px solid var(--border-hairline)", opacity: 0.6 }}
                onClick={() => setViewingUser(u)}
                data-testid={`user-row-${u.email}`}
              >
                <div className="min-w-0">
                  <div
                    className="text-xs truncate"
                    style={{ color: "var(--fg-raw)", fontFamily: "var(--font-display)", fontSize: "0.9375rem" }}
                  >
                    {u.displayName || u.email.split("@")[0]}
                  </div>
                  <div
                    className="truncate"
                    style={{
                      color: "var(--text-muted)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--type-label-size)",
                      letterSpacing: "var(--type-label-tracking)",
                      textTransform: "uppercase",
                    }}
                  >
                    {u.email}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="shrink-0"
                    style={{
                      color: "var(--text-muted)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--type-label-size)",
                      letterSpacing: "var(--type-label-tracking)",
                      textTransform: "uppercase",
                    }}
                  >
                    {u.totalMessages > 0 ? (
                      <><span style={{ fontFamily: "var(--font-display)" }}>{u.totalMessages}</span> MSGS</>
                    ) : (
                      <>JOINED {formatDate(u.createdAt)}</>
                    )}
                  </span>
                  {u.totalMessages > 0 && <MessageCircle size={11} style={{ color: "var(--text-muted)" }} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </AdminAtmosphere>
  );
}

function ConversationView({ user, onBack }: { user: UserEntry; onBack: () => void }) {
  const { data, isLoading } = useQuery<{ messages: ChatMsg[] }>({
    queryKey: ["/api/admin/user", user.id, "messages"],
    queryFn: () => fetch(`/api/admin/user/${user.id}/messages`, { credentials: "include" }).then(r => r.json()),
  });

  const messages = data?.messages || [];

  let currentDay = "";

  return (
    <div className="admin-page-shell w-full max-w-lg mx-auto px-5 py-6" data-testid="admin-conversation-view">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-1"
          style={{ color: "var(--text-muted)" }}
          data-testid="button-conversation-back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div
            className="text-sm truncate"
            style={{ color: "var(--fg-raw)", fontFamily: "var(--font-display)", fontSize: "0.9375rem" }}
          >
            {user.displayName || user.email.split("@")[0]}
          </div>
          <div
            className="truncate"
            style={{
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontSize: "var(--type-label-size)",
              letterSpacing: "var(--type-label-tracking)",
              textTransform: "uppercase",
            }}
          >
              {user.email} · <span style={{ fontFamily: "var(--font-display)" }}>{user.totalMessages}</span> MESSAGES
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <p
            style={{
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontSize: "var(--type-label-size)",
              letterSpacing: "var(--type-label-tracking)",
            }}
          >
            LOADING
          </p>
        </div>
      ) : messages.length === 0 ? (
        <p
          className="text-center py-8"
          style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
        >
          No messages yet
        </p>
      ) : (
        <div className="space-y-3 pb-8">
          {messages.map((msg, i) => {
            const msgDay = new Date(msg.createdAt).toDateString();
            let showDay = false;
            if (msgDay !== currentDay) {
              currentDay = msgDay;
              showDay = true;
            }

            return (
              <div key={i}>
                {showDay && (
                  <div className="text-center py-3">
                    <span
                      className="px-3 py-1 rounded-full"
                      style={{
                        color: "var(--text-muted)",
                        fontFamily: "var(--font-mono)",
                        fontSize: "var(--type-label-size)",
                        letterSpacing: "var(--type-label-tracking)",
                        textTransform: "uppercase",
                        background: "var(--bg-surface)",
                      }}
                    >
                      {formatDayHeader(msg.createdAt)}
                    </span>
                  </div>
                )}
                <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className="max-w-[85%] rounded-2xl px-3.5 py-2.5"
                    style={{
                      background: msg.role === "user"
                        ? "var(--accent-bg-strong)"
                        : "var(--card-bg)",
                      border: msg.role === "user"
                        ? "1px solid var(--accent-border-strong)"
                        : "1px solid var(--border-hairline)",
                    }}
                  >
                    <p
                      className="leading-relaxed whitespace-pre-wrap"
                      style={{
                        color: "var(--fg-raw)",
                        fontFamily: "var(--font-reflective)",
                        fontSize: "0.9375rem",
                        fontWeight: 400,
                      }}
                    >
                      {msg.role === "assistant" ? formatTypographicQuotes(msg.content) : msg.content}
                    </p>
                    <div className="mt-1">
                      <span
                        style={{
                          color: "var(--text-muted)",
                          fontFamily: "var(--font-mono)",
                          fontSize: "var(--type-label-size)",
                          letterSpacing: "var(--type-label-tracking)",
                        }}
                      >
                        {formatTime(msg.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="text-center">
      <div
        style={{
          color: accent ? "var(--accent-text)" : "var(--fg-raw)",
          fontFamily: "var(--font-display)",
          fontSize: "1.125rem",
          fontWeight: 600,
        }}
      >
        <AnimNum value={value} />
      </div>
      <div
        style={{
          color: "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          fontSize: "var(--type-label-size)",
          letterSpacing: "var(--type-label-tracking)",
          textTransform: "uppercase",
          marginTop: "2px",
        }}
      >
        {label}
      </div>
    </div>
  );
}

function UserRow({ u, periodLabel, rank, onView }: { u: UserEntry; periodLabel: string; rank: number; onView: () => void }) {
  const queryClient = useQueryClient();
  const maxBar = 100;
  const barW = Math.min(Math.max((u.recentMessages / maxBar) * 100, 8), 100);

  return (
    <div
      className="rounded-lg px-3 py-2.5 cursor-pointer transition-all"
      style={{ background: "var(--card-bg)", border: "1px solid var(--border-hairline)" }}
      onClick={onView}
      data-testid={`user-row-${u.email}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className="w-4 shrink-0"
              style={{
                color: "var(--text-muted)",
                fontFamily: "var(--font-display)",
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              {rank}
            </span>
            <span
              className="text-sm truncate"
              style={{ color: "var(--fg-raw)", fontFamily: "var(--font-display)", fontSize: "0.9375rem" }}
            >
              {u.displayName || u.email.split("@")[0]}
            </span>
            {u.hitLimit && (
              <button
                className="px-1.5 py-0.5 rounded-full shrink-0 flex items-center gap-1"
                style={{
                  background: "var(--color-loss-chip-bg)",
                  color: "var(--color-loss)",
                  fontFamily: "var(--font-display)",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await apiRequest("POST", `/api/admin/user/${u.id}/reset-limit`);
                    queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
                  } catch {}
                }}
                title="Reset daily limit"
                data-testid={`button-reset-limit-${u.id}`}
              >
                limit {u.dailyMessageCount}/20
                <RotateCcw size={8} />
              </button>
            )}
          </div>
          <div
            className="ml-6 truncate"
            style={{
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontSize: "var(--type-label-size)",
              letterSpacing: "var(--type-label-tracking)",
            }}
          >
            {u.email}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right shrink-0">
            <div
              style={{
                color: "var(--accent-text)",
                fontFamily: "var(--font-display)",
                fontSize: "0.9375rem",
                fontWeight: 500,
              }}
            >
              {u.recentMessages}
            </div>
            <div
              style={{
                color: "var(--text-muted)",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--type-label-size)",
                letterSpacing: "var(--type-label-tracking)",
              }}
            >
              in {periodLabel}
            </div>
          </div>
          <MessageCircle size={13} style={{ color: "var(--text-muted)", opacity: 0.5 }} />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-1.5 ml-6">
        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: "var(--bg-surface)" }}>
          <div
            className="h-full rounded-full"
            style={{ width: `${barW}%`, background: "var(--accent-text)", opacity: 0.5, transition: "width 0.4s ease" }}
          />
        </div>
        <span
          className="shrink-0"
          style={{
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--type-label-size)",
            letterSpacing: "var(--type-label-tracking)",
            textTransform: "uppercase",
          }}
        >
          <span style={{ fontFamily: "var(--font-display)" }}>{u.totalMessages}</span> TOTAL · {formatDate(u.createdAt)}
        </span>
      </div>
    </div>
  );
}
